import type { ReactNode } from "react";
import { ArrowDown, ArrowRight, ArrowUp, type LucideIcon } from "lucide-react";
import { Card, CardContent } from "./card";
import { cn } from "../lib/utils";

type TrendDirection = "up" | "down" | "neutral";

interface StatCardProps {
  /** Metric label, e.g. "Active users". Pass an already-translated string. */
  label: string;
  /** The metric value. Accepts a node so callers can format/skeleton it. */
  value: ReactNode;
  /** Optional Lucide icon shown top-right. */
  icon?: LucideIcon;
  /** Optional muted hint shown under the value. */
  hint?: string;
  /** Optional delta indicator. `value` is a pre-formatted string, e.g. "+12%". */
  trend?: { value: string; direction: TrendDirection };
  className?: string;
}

const TREND_ICON: Record<TrendDirection, LucideIcon> = {
  up: ArrowUp,
  down: ArrowDown,
  neutral: ArrowRight,
};

// Token-only: no new color palette is introduced. "down" is the attention/
// negative case and consumes the reserved accent (UI-SPEC accent role #2 —
// "attention text (e.g. '2 need evidence' trend lines)"); "up" the strong
// foreground token, "neutral" muted.
const TREND_COLOR: Record<TrendDirection, string> = {
  up: "text-foreground",
  down: "text-primary",
  neutral: "text-muted-foreground",
};

/**
 * Compact metric card designed to sit in a responsive grid (NOT a full-width
 * box). Shows a small muted label, a prominent value, an optional icon, and an
 * optional trend delta.
 *
 * Presentational only — pass already-translated strings.
 */
export function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  trend,
  className,
}: StatCardProps) {
  const TrendIcon = trend ? TREND_ICON[trend.direction] : null;

  return (
    <Card className={cn("gap-0 py-0", className)}>
      <CardContent className="flex flex-col gap-2 p-3.5">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-[11px] font-semibold">
            {label}
          </span>
          {Icon && (
            <Icon
              className="text-muted-foreground size-4 shrink-0"
              aria-hidden="true"
            />
          )}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-[22px] font-bold tabular-nums">{value}</span>
          {trend && TrendIcon && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-[11.5px] font-medium tabular-nums",
                TREND_COLOR[trend.direction],
              )}
            >
              <TrendIcon className="size-3" aria-hidden="true" />
              {trend.value}
            </span>
          )}
        </div>
        {hint && (
          <span className="text-muted-foreground text-[12.5px] text-pretty">
            {hint}
          </span>
        )}
      </CardContent>
    </Card>
  );
}

export type { StatCardProps };
